import { findEventEntry } from "@repo/vihangam-yog-shared";

import { CHAPTER } from "../../chapter";
import { isEventToday } from "../../lib/eventTiming";

import {
  currencyOf,
  formatKunds,
  kundCountingFor,
  kundTallyOf,
  type KundTally,
} from "./guestFormat";
import { isNotComing } from "./notComing";
import { formatMoney, paidSummaryOf, type EventGroup } from "./shared";

/** One number in the strip, read as "People coming, 24". */
function Figure({
  label,
  value,
  tone = "ink",
  kunds,
}: {
  label: string;
  value: string;
  tone?: "ink" | "amber";
  /** A Kund event's tally, drawn as a slim bar under the number. */
  kunds?: KundTally;
}) {
  return (
    <div className={`flex flex-col ${kunds ? "min-w-40" : "min-w-28"}`}>
      <dt className="order-2 mt-1.5 text-[14px] font-medium text-[color:var(--color-ink-soft)]">
        {label}
      </dt>
      <dd className="order-1">
        <span
          className={`stat-value block ${
            tone === "amber"
              ? "text-[color:var(--color-amber)]"
              : "text-[color:var(--color-ink)]"
          }`}
        >
          {value}
        </span>
        {kunds ? (
          <span
            aria-hidden="true"
            className="mt-2 block h-2 w-full overflow-hidden rounded-full bg-[color:var(--color-ink)]/10"
          >
            <span
              className="block h-full rounded-full bg-[color:var(--color-amber)]"
              style={{
                width: `${Math.min(100, (kunds.booked / kunds.total) * 100)}%`,
              }}
            />
          </span>
        ) : null}
      </dd>
    </div>
  );
}

const STRIP =
  "mt-6 flex flex-wrap gap-x-10 gap-y-5 border-y border-[color:var(--color-line)] py-5";

/**
 * The event at a glance, at most four numbers (five on the day, with
 * Arrived). A Hawan shows Kunds, Families, People coming and Money
 * received; other paid events People coming, Bookings and Money received;
 * free events People coming and Bookings. Everything else is one tap
 * away under "More about this event". "All events" shows the totals only.
 */
export function AtAGlance({
  group,
  allTotals,
  now,
}: {
  group: EventGroup | null;
  allTotals: { bookings: number; people: number };
  now: number;
}) {
  if (!group) {
    return (
      <dl className={STRIP}>
        <Figure label="People coming" value={String(allTotals.people)} />
        <Figure label="Bookings" value={String(allTotals.bookings)} />
      </dl>
    );
  }

  const regs = group.registrations;
  const price = findEventEntry(group.eventSlug)?.price;
  const paid = paidSummaryOf(regs);
  // Families are the paid bookings still coming; a seva booking's payment
  // stays in Money received.
  const families = paidSummaryOf(regs.filter((r) => !isNotComing(r))).paidCount;
  const kunds = kundTallyOf(regs, price, kundCountingFor(CHAPTER.id));
  const showMoney = price !== undefined || paid.isPaid;
  const showArrived =
    isEventToday(group, new Date(now)) || group.checkedInPeople > 0;

  const people = (
    <Figure
      label="People coming"
      value={String(group.totalPeople)}
      tone="amber"
    />
  );
  const money = showMoney ? (
    <Figure
      label="Money received"
      value={formatMoney(paid.collectedCents, currencyOf(group.eventSlug))}
    />
  ) : null;
  const arrived = showArrived ? (
    <Figure
      label="Arrived"
      value={`${group.checkedInPeople} of ${group.totalPeople}`}
    />
  ) : null;

  if (kunds) {
    return (
      <dl className={STRIP}>
        <Figure
          label="Kunds"
          value={`${formatKunds(kunds.booked)} of ${kunds.total}`}
          kunds={kunds}
        />
        <Figure label="Families" value={String(families)} />
        {people}
        {money}
        {arrived}
      </dl>
    );
  }

  return (
    <dl className={STRIP}>
      {people}
      <Figure label="Bookings" value={String(group.registrationCount)} />
      {money}
      {arrived}
    </dl>
  );
}
